import React, { useState, useEffect } from 'react';
import { auth } from '../../../../../firebase/config';
import Modal from '../../../../../components/Modal';
import TextField from '../../../../../components/TextField';
import Button from '../../../../../components/Button';
import { FiMapPin, FiPhone, FiHash, FiLock } from 'react-icons/fi';
import Icon from '../../../../../components/Icon';
import SearchableSelect from '../../../../../components/SearchableSelect';
import {
    fetchLocations,
    fetchBankInfo,
    fetchManagers,
    submitBranch,
    type Location,
    type Bank,
    type Manager,
    type BranchFormData
} from '../service/addBranchService';

type PSO = {
    id: string;
    name: string;
    psoId?: string;
};

type AddBranchModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    bankId?: string;
};

export default function AddBranchModal({ isOpen, onClose, onSuccess, bankId }: AddBranchModalProps) {
    const [loading, setLoading] = useState(false);
    const [locations, setLocations] = useState<Location[]>([]);
    const [banks, setBanks] = useState<Bank[]>([]);
    const [managers, setManagers] = useState<Manager[]>([]);
    const [psos, setPsos] = useState<PSO[]>([]);
    const [selectedBankId, setSelectedBankId] = useState<string>('');
    const [formData, setFormData] = useState<BranchFormData>({
        branchCode: '',
        address: '',
        mobile: '',
        locationId: '',
        securityCode: '',
        psoId: '',
        branchManagerId: '',
        operationalManagerId: '',
        areaManagerId: '',
        customLocationName: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!isOpen) return;

        const loadData = async () => {
            try {
                // Load locations
                const locList = await fetchLocations();
                setLocations(locList);

                // Use provided bankId or find it from uid
                const uid = auth.currentUser?.uid;
                if (!uid) return;

                // Use provided bankId or uid as fallback
                const actualBankId = bankId || uid;
                setSelectedBankId(actualBankId);

                // Load bank info for display
                const bankInfo = await fetchBankInfo(actualBankId);
                if (bankInfo) {
                    setBanks([bankInfo]);
                }
            } catch (err) {
                console.error('Error loading data:', err);
            }
        };

        loadData();
    }, [isOpen, bankId]);

    // Load managers when bank is selected
    useEffect(() => {
        if (!selectedBankId) {
            setManagers([]);
            return;
        }

        const loadManagers = async () => {
            try {
                const managerList = await fetchManagers(selectedBankId);
                setManagers(managerList);

                // Load PSOs
                const { collection, getDocs } = await import('firebase/firestore');
                const { db } = await import('../../../../../firebase/config');
                const psoSnap = await getDocs(collection(db, 'banks', selectedBankId, 'pos'));
                const psoList = psoSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                } as PSO));
                setPsos(psoList);
            } catch (err) {
                console.error('Error loading operational data:', err);
                setManagers([]);
                setPsos([]);
            }
        };

        loadManagers();
    }, [selectedBankId]);

    const handleInputChange = (field: string, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.branchCode.trim()) {
            newErrors.branchCode = 'Branch code is required';
        }
        if (!formData.address.trim()) {
            newErrors.address = 'Address is required';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile is required';
        }
        if (!formData.locationId) {
            newErrors.locationId = 'Location is required';
        }
        if (!formData.securityCode.trim()) {
            newErrors.securityCode = 'Client code is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const uid = auth.currentUser?.uid;
        if (!uid || !selectedBankId) return;

        setLoading(true);
        try {
            await submitBranch(selectedBankId, formData);

            // Reset form
            setFormData({
                branchCode: '',
                address: '',
                mobile: '',
                locationId: '',
                securityCode: '',
                psoId: '',
                branchManagerId: '',
                operationalManagerId: '',
                areaManagerId: '',
                customLocationName: '',
            });
            setErrors({});

            onSuccess();
        } catch (err: any) {
            console.error('Error adding branch:', err);
            setErrors({ submit: err.message || 'Failed to add branch. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            branchCode: '',
            address: '',
            mobile: '',
            locationId: '',
            securityCode: '',
            psoId: '',
            branchManagerId: '',
            operationalManagerId: '',
            areaManagerId: '',
            customLocationName: '',
        });
        setErrors({});
        // Don't reset selectedBankId - keep it for next time
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add New Branch"
            size="lg"
        >
            <form onSubmit={handleSubmit} className="p-6">
                <div className="space-y-4">
                    {banks.length > 0 && (
                        <div>
                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Bank
                            </label>
                            <input
                                type="text"
                                value={banks[0]?.name || ''}
                                readOnly
                                className="w-full rounded-xl border border-gray-300 bg-gray-100 py-3 pl-4 pr-4 text-sm outline-none dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
                            />
                        </div>
                    )}

                    <TextField
                        label="Branch Code"
                        value={formData.branchCode}
                        onChange={(e) => handleInputChange('branchCode', e.target.value)}
                        placeholder="Enter branch code"
                        required
                        prefix={<Icon icon={FiHash} className="text-gray-400" />}
                        className={errors.branchCode ? 'border-red-500' : ''}
                    />
                    {errors.branchCode && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.branchCode}</p>
                    )}

                    <TextField
                        label="Client Code"
                        value={formData.securityCode}
                        onChange={(e) => handleInputChange('securityCode', e.target.value)}
                        placeholder="Enter client code"
                        required
                        prefix={<Icon icon={FiLock} className="text-gray-400" />}
                        className={errors.securityCode ? 'border-red-500' : ''}
                    />
                    {errors.securityCode && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.securityCode}</p>
                    )}

                    <TextField
                        label="Mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        placeholder="Enter mobile number"
                        required
                        prefix={<Icon icon={FiPhone} className="text-gray-400" />}
                        className={errors.mobile ? 'border-red-500' : ''}
                    />
                    {errors.mobile && (
                        <p className="-mt-3 text-xs text-red-600 dark:text-red-400">{errors.mobile}</p>
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Location
                        </label>
                        <div className="relative">
                            <Icon
                                icon={FiMapPin}
                                className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                            />
                            <select
                                value={formData.locationId}
                                onChange={(e) => handleInputChange('locationId', e.target.value)}
                                className={`w-full rounded-xl border py-3 pl-10 pr-4 text-sm outline-none transition bg-white text-gray-900 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.locationId ? 'border-red-500' : ''}`}
                            >
                                <option value="">Select Location</option>
                                {locations.map(loc => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name}
                                    </option>
                                ))}
                                <option value="custom">+ Other (Enter Custom)</option>
                            </select>
                        </div>
                        {errors.locationId && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.locationId}</p>
                        )}
                    </div>

                    {formData.locationId === 'custom' && (
                        <TextField
                            label="Custom Location Name"
                            value={formData.customLocationName || ''}
                            onChange={(e) => handleInputChange('customLocationName', e.target.value)}
                            placeholder="Enter custom location name"
                            required
                        />
                    )}

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Address
                        </label>
                        <textarea
                            value={formData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            placeholder="Enter branch address"
                            rows={3}
                            required
                            className={`w-full rounded-xl border py-3 px-4 text-sm outline-none transition bg-white text-gray-900 placeholder:text-gray-400 border-gray-300 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600 ${errors.address ? 'border-red-500' : ''}`}
                        />
                        {errors.address && (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.address}</p>
                        )}
                    </div>

                    <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                            Assign PSO
                        </label>
                        <select
                            value={formData.psoId || ''}
                            onChange={(e) => handleInputChange('psoId', e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                        >
                            <option value="">Select PSO</option>
                            {psos.map(pso => (
                                <option key={pso.id} value={(pso as any).psoId || pso.id}>
                                    {pso.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Manager Selection */}
                    {selectedBankId && managers.length > 0 && (
                        <div className="mt-6 space-y-4 border-t border-gray-200 pt-4 dark:border-gray-700">
                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                Manager Assignments (Optional)
                            </h3>

                            <SearchableSelect
                                label="Branch Manager"
                                options={managers.map(mgr => ({
                                    id: mgr.id,
                                    label: mgr.name,
                                    value: mgr.id,
                                    details: {
                                        email: mgr.email,
                                        mobile: mgr.mobile,
                                    },
                                }))}
                                value={formData.branchManagerId || ''}
                                onChange={(value) => handleInputChange('branchManagerId', value)}
                                placeholder="Select Branch Manager (Optional)"
                            />

                            <SearchableSelect
                                label="Operational Manager"
                                options={managers.map(mgr => ({
                                    id: mgr.id,
                                    label: mgr.name,
                                    value: mgr.id,
                                    details: {
                                        email: mgr.email,
                                        mobile: mgr.mobile,
                                    },
                                }))}
                                value={formData.operationalManagerId || ''}
                                onChange={(value) => handleInputChange('operationalManagerId', value)}
                                placeholder="Select Operational Manager (Optional)"
                            />

                            <SearchableSelect
                                label="Area Manager"
                                options={managers.map(mgr => ({
                                    id: mgr.id,
                                    label: mgr.name,
                                    value: mgr.id,
                                    details: {
                                        email: mgr.email,
                                        mobile: mgr.mobile,
                                    },
                                }))}
                                value={formData.areaManagerId || ''}
                                onChange={(value) => handleInputChange('areaManagerId', value)}
                                placeholder="Select Area Manager (Optional)"
                            />
                        </div>
                    )}

                    {errors.submit && (
                        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
                            {errors.submit}
                        </div>
                    )}
                </div>

                <div className="mt-6 flex gap-3">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        className="flex-1"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        className="flex-1"
                        disabled={loading}
                    >
                        {loading ? 'Adding...' : 'Add Branch'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}

